<script lang="ts">
	import Megaphone from '@lucide/svelte/icons/megaphone'
	import Search from '@lucide/svelte/icons/search'
	import StarPlus from '@lucide/svelte/icons/star-plus'
	import { debounce } from 'es-toolkit/function'
	import { cn } from 'tailwind-variants'

	import { resolve } from '$app/paths'
	import { page } from '$app/state'
	import ChainBadge from '#lib/components/chain-badge.svelte'
	import SearchInput from '#lib/components/search-input.svelte'
	import { Badge } from '#lib/components/ui/badge/index.js'
	import { Button } from '#lib/components/ui/button/index.js'
	import * as NavigationMenu from '#lib/components/ui/navigation-menu/index.js'
	import {
		handleMenuClick,
		isMenuInternal,
		isSectionRegular,
		MENU_SECTIONS,
	} from '#lib/constants/menuItems.js'
	import { m } from '#lib/paraglide/messages.js'
	import { mobileMenu, updateMobileMenu, updateSearchDialog } from '#lib/utils/dialogs.svelte.js'
	import { hashToOption, phg } from '#lib/utils/posthog.js'

	let { class: className }: { class?: string } = $props()

	let search = $state('')

	const option = $derived(hashToOption(page.data.user, 2))
</script>

<header
	class={cn('border-border/50 relative z-4 w-full border-b backdrop-blur-xs', className, {
		'bg-background': mobileMenu.open,
	})}
>
	<nav class="mx-auto flex h-16 items-center px-3 xl:container">
		<a
			class="text-branding-gradient text-xl font-bold transition-opacity hover:opacity-80"
			href={resolve('/(marketing)')}
		>
			Chainspect
		</a>

		<Button
			class="ml-auto md:hidden"
			aria-label="Search"
			onclick={() => updateSearchDialog(true)}
			size="icon"
			variant="ghost"
		>
			<Search />
		</Button>

		<Button
			class="ml-1 md:hidden"
			aria-label={mobileMenu.open ? 'Collapse menu' : 'Expand menu'}
			onclick={e => {
				updateMobileMenu(!mobileMenu.open, e.currentTarget)

				if (mobileMenu.open) phg.capture('mobile_menu_open')
			}}
			size="icon"
			type="button"
			variant="ghost"
		>
			<span
				class={cn(
					'bg-foreground absolute h-0.5 w-5 rounded-xs transition duration-100 ease-in-out',
					mobileMenu.open ? 'rotate-45' : '-translate-y-1.5'
				)}
			></span>
			<span
				class={cn(
					'bg-foreground absolute h-0.5 w-5 rounded-xs transition duration-100 ease-in-out',
					mobileMenu.open ? 'opacity-0' : 'opacity-100'
				)}
			></span>
			<span
				class={cn(
					'bg-foreground absolute h-0.5 w-5 rounded-xs transition duration-100 ease-in-out',
					mobileMenu.open ? '-rotate-45' : 'translate-y-1.5'
				)}
			></span>
		</Button>

		<NavigationMenu.Root
			class="ml-auto hidden h-full items-center md:flex lg:mr-auto"
			viewport={false}
		>
			<NavigationMenu.List>
				{#each MENU_SECTIONS.filter(({ id }) => id !== 'compare' && id !== 'community' && id !== 'chains') as section (section.text)}
					<NavigationMenu.Item>
						<NavigationMenu.Trigger>
							{section.text}
						</NavigationMenu.Trigger>

						{#if isSectionRegular(section)}
							<NavigationMenu.Content>
								<ul class="flex flex-col gap-2">
									{#each section.items as item (item.text)}
										<li>
											<NavigationMenu.Link
												class="flex-row items-center gap-1.5 whitespace-nowrap"
												onclick={e =>
													handleMenuClick(e, section, item, page.url.pathname, 'header')}
												{...isMenuInternal(item)
													? { href: resolve(item.route) }
													: {
															href: item.href,
															rel: 'external noopener noreferrer',
															target: '_blank',
														}}
											>
												<item.Icon />

												{item.text}

												{#if item.new}
													<Badge class="font-bold uppercase" size="4xs" variant="brand">New</Badge>
												{/if}
											</NavigationMenu.Link>
										</li>
									{/each}
								</ul>
							</NavigationMenu.Content>
						{:else}
							<NavigationMenu.Content class="max-w-64 min-w-64">
								<SearchInput
									class="mb-2"
									onkeyup={debounce(
										() =>
											search.trim() !== '' &&
											phg.capture('search_chains', { value: search.trim() }),
										1000
									)}
									placeholder={m.search_networks()}
									bind:value={search}
								/>

								<ul class="flex max-h-96 flex-col gap-0.5 overflow-auto">
									{#each section.chains.filter(c => c.name
											.toLowerCase()
											.includes(search.toLowerCase())) as chain (chain.id)}
										<li>
											<NavigationMenu.Link
												class="flex-row items-center gap-1.5 p-1.5 text-sm whitespace-nowrap"
												href={resolve('/(app)/chain/[slug]', { slug: chain.slug })}
											>
												<img
													class="size-5"
													alt="{chain.name} logo"
													crossorigin="anonymous"
													decoding="async"
													fetchpriority="low"
													height="16"
													loading="lazy"
													src="https://res.cloudinary.com/dcruwhddg/image/upload/icons/{chain.slug}.svg"
													width="16"
												/>

												{chain.name}

												<ChainBadge {chain} />
											</NavigationMenu.Link>
										</li>
									{:else}
										<li class="mx-2 text-sm">Oops, no such networks...</li>
									{/each}
								</ul>
							</NavigationMenu.Content>
						{/if}
					</NavigationMenu.Item>
				{/each}
			</NavigationMenu.List>
		</NavigationMenu.Root>

		{#if option === 1}
			<Button
				class="hidden lg:flex"
				href="https://tally.so/r/44qM7k"
				rel="external noopener noreferrer"
				target="_blank"
				variant="glowing"
			>
				<Megaphone />

				{m.menu_advertise()}
			</Button>
		{:else}
			<Button
				class="hidden lg:flex"
				href="https://tally.so/r/n058X6"
				rel="external noopener noreferrer"
				target="_blank"
				variant="glowing"
			>
				<StarPlus />

				{m.menu_get_listed()}
			</Button>
		{/if}
	</nav>
</header>
